import React from 'react';
import { Box, Skeleton } from '@mui/material';

// Waiting states in elebyte.io's idiom: the E, rippling in skewed rings (the site's loader glyph), and
// skeletons shaped like the ruled rows and tiles they stand in for, not like cards.

export function PageLoader({ message = 'Loading...' }) {
  return (
    <div className="sp-loader" role="status" aria-live="polite">
      <span className="sp-loader__e" aria-hidden="true"><img src="/glyph-e-cap.svg" alt="" /></span>
      <span className="sp-loader__label">{message}</span>
    </div>
  );
}

const rule = { borderTop: '1px solid rgba(255, 255, 255, 0.12)' };

// Skeleton for an article row
export function ArticleCardSkeleton() {
  return (
    <Box sx={{ ...rule, py: 2.75 }}>
      <Skeleton variant="text" width="60%" height={34} />
      <Skeleton variant="text" width="90%" />
      <Skeleton variant="text" width={90} height={16} sx={{ mt: 1 }} />
    </Box>
  );
}

// Skeleton for a category tile
export function CategoryCardSkeleton() {
  return (
    <Box sx={{ ...rule, pt: 3, pb: 4 }}>
      <Skeleton variant="text" width={80} height={16} />
      <Skeleton variant="text" width="65%" height={40} />
      <Skeleton variant="text" width="90%" />
    </Box>
  );
}

// Skeleton for ticket row
export function TicketRowSkeleton() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
      <Skeleton variant="text" width={90} height={16} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton variant="text" width="40%" />
      </Box>
      <Skeleton variant="text" width={80} height={16} />
    </Box>
  );
}

// Skeleton for article content
export function ArticleContentSkeleton() {
  return (
    <Box sx={{ pt: { xs: 5, md: 12 } }}>
      <Skeleton variant="text" width={220} height={16} sx={{ mb: 3 }} />
      <Skeleton variant="text" width="85%" height={72} />
      <Skeleton variant="text" width="55%" height={72} sx={{ mb: 4 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 6 }}>
        <Skeleton variant="text" width={90} height={16} />
        <Skeleton variant="text" width={120} height={16} />
      </Box>
      {['100%', '100%', '92%', '96%', '70%'].map((w, i) => <Skeleton key={i} variant="text" width={w} height={26} />)}
    </Box>
  );
}

// Skeleton for article list
export function ArticleListSkeleton({ count = 5 }) {
  return (
    <Box>
      {[...Array(count)].map((_, i) => <ArticleCardSkeleton key={i} />)}
    </Box>
  );
}

// Skeleton for ticket list
export function TicketListSkeleton({ count = 5 }) {
  return (
    <Box>
      {[...Array(count)].map((_, i) => (
        <Box key={i} sx={{ ...rule, py: 2.75, display: 'flex', justifyContent: 'space-between', gap: 3 }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Skeleton variant="text" width={110} height={16} />
            <Skeleton variant="text" width="60%" height={34} />
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              <Skeleton variant="text" width={70} height={16} />
              <Skeleton variant="text" width={60} height={16} />
            </Box>
          </Box>
          <Skeleton variant="text" width={110} height={16} />
        </Box>
      ))}
    </Box>
  );
}

// Skeleton for ticket detail
export function TicketDetailSkeleton() {
  return (
    <Box sx={{ pt: { xs: 5, md: 12 } }}>
      <Skeleton variant="text" width={120} height={16} sx={{ mb: 2 }} />
      <Skeleton variant="text" width="60%" height={64} sx={{ mb: 3 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
        <Skeleton variant="rounded" width={150} height={42} />
        <Skeleton variant="rounded" width={120} height={42} />
      </Box>
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="95%" />
      <Skeleton variant="text" width="80%" sx={{ mb: 6 }} />
      {[...Array(3)].map((_, i) => (
        <Box key={i} sx={{ borderLeft: '2px solid rgba(102, 224, 250, 0.3)', pl: 2, mb: 3 }}>
          <Skeleton variant="text" width={140} height={16} />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="50%" />
        </Box>
      ))}
    </Box>
  );
}

// Skeleton for category list
export function CategoryListSkeleton({ count = 4 }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', columnGap: 4 }}>
      {[...Array(count)].map((_, i) => <CategoryCardSkeleton key={i} />)}
    </Box>
  );
}

export default PageLoader;
